import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Nav, NavItem, NavLink, TabContent, TabPane } from '@/components/ui-compat'
import { Settings, FileText, Tag, Layers, Package as PackageIcon, Plus, Pencil, Check, X, LoaderCircle, ChevronUp, ChevronDown, ListChecks, Workflow } from 'lucide-react'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import WorkflowTemplatesTab from './WorkflowTemplatesTab'

interface LookupValue {
  id: string
  kind: string
  value: string
  sortOrder: number
  archived: boolean
}

// Mirrors the backend `enum SubmittalLookupKind` (src/graphql/submittals/submittal.ts).
// Spec Sections reuses the list page's own tab label — same word for the same
// concept in two places in this module. The other four kinds have no existing
// plural label to reuse, so they get dedicated `Submittals_lookup_tab_*` keys.
const LOOKUP_TABS: { kind: string; labelKey: string; Icon: any }[] = [
  { kind: 'SPEC_SECTION', labelKey: 'Submittals_tab_spec_sections', Icon: FileText },
  { kind: 'CATEGORY', labelKey: 'Submittals_lookup_tab_categories', Icon: Tag },
  { kind: 'SUBCATEGORY', labelKey: 'Submittals_lookup_tab_subcategories', Icon: Tag },
  { kind: 'DISCIPLINE', labelKey: 'Submittals_lookup_tab_disciplines', Icon: Layers },
  { kind: 'PACKAGE', labelKey: 'Submittals_lookup_tab_packages', Icon: PackageIcon },
]

interface LookupRowProps {
  row: LookupValue
  busy: boolean
  disabled: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onStartEdit: () => void
  onToggleArchived: () => void
}

const LookupRow: React.FC<LookupRowProps> = ({
  row, busy, disabled, canMoveUp, canMoveDown, onMoveUp, onMoveDown, onStartEdit, onToggleArchived,
}) => {
  const { t } = useTranslation()
  return (
    <div className={cn('flex items-center gap-2 rounded border px-3 py-2', row.archived && 'bg-muted/40')}>
      <div className="flex flex-col shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="h-4 w-6 p-0"
          onClick={onMoveUp}
          disabled={disabled || !canMoveUp}
          title={t('Submittals_lookup_move_up')}
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-4 w-6 p-0"
          onClick={onMoveDown}
          disabled={disabled || !canMoveDown}
          title={t('Submittals_lookup_move_down')}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </div>
      <span className={cn('flex-1 truncate text-sm', row.archived && 'text-muted-foreground line-through')}>
        {row.value}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 shrink-0"
        onClick={onStartEdit}
        disabled={disabled}
        title={t('Common_edit')}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      {busy ? (
        <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <Switch
          checked={!row.archived}
          onCheckedChange={onToggleArchived}
          disabled={disabled}
          title={row.archived ? t('Submittals_lookup_archived') : t('Submittals_lookup_active')}
        />
      )}
    </div>
  )
}

interface SubmittalLookupsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  /** Called after every successful mutation so the list page's own (non-archived)
   *  lookups query — used by the filter bar and the create/edit forms — picks up
   *  renames/additions/archives without needing a full page reload. */
  onChanged?: () => void
}

/**
 * ADMIN-only management of the five SubmittalLookupKind value lists (spec
 * sections, categories, subcategories, disciplines, packages). One dialog,
 * five compat tabs — same value-list shape per kind, so a single row/add-row
 * renderer is reused across all five rather than five near-identical panels.
 */
const SubmittalLookupsDialog: React.FC<SubmittalLookupsDialogProps> = ({
  open,
  onOpenChange,
  projectId,
  onChanged,
}) => {
  const { t } = useTranslation()
  const [dialogTab, setDialogTab] = useState<string>('lookups')
  const [tab, setTab] = useState<string>(LOOKUP_TABS[0].kind)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [newValue, setNewValue] = useState('')
  // The single in-flight mutation's target: a lookup value id, or 'NEW' for the
  // add-row — never more than one at a time, since every trigger disables the
  // whole panel while busy.
  const [busyId, setBusyId] = useState<string | null>(null)

  const { data, loading, refetch } = useQuery(Query.SUBMITTAL_LOOKUP_VALUES, {
    variables: { projectId, includeArchived: true },
    skip: !projectId,
  })
  const [upsertLookup] = useMutation(Mutation.UPSERT_SUBMITTAL_LOOKUP_VALUE)
  const [archiveLookup] = useMutation(Mutation.ARCHIVE_SUBMITTAL_LOOKUP_VALUE)

  const lookups: LookupValue[] = data?.submittalLookupValues || []
  const rows = lookups.filter((l) => l.kind === tab)

  const handleClose = () => {
    if (busyId) return
    onOpenChange(false)
  }

  const handleTabChange = (kind: string) => {
    setTab(kind)
    setEditingId(null)
    setNewValue('')
  }

  const startEdit = (row: LookupValue) => {
    setEditingId(row.id)
    setEditValue(row.value)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditValue('')
  }

  const afterMutate = async () => {
    await refetch()
    onChanged?.()
  }

  const handleSaveEdit = async (row: LookupValue) => {
    const trimmed = editValue.trim()
    if (!trimmed) return
    setBusyId(row.id)
    try {
      await upsertLookup({
        variables: { projectId, kind: row.kind, value: trimmed, id: row.id, sortOrder: row.sortOrder },
      })
      await afterMutate()
      toastr.success('', t('Submittals_lookup_updated'))
      setEditingId(null)
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setBusyId(null)
    }
  }

  /**
   * Swaps `row` with its neighbor in the given direction by writing each row's
   * target ARRAY INDEX as its new sortOrder — not by exchanging their stored
   * sortOrder values. New lookup values are created with sortOrder 0 (backend
   * default), so every row in a fresh tab ties at 0 and swapping the stored
   * values is a no-op (0 <-> 0 forever). Writing indices instead self-heals
   * that legacy/all-zero data on the first swap and stays idempotent once the
   * list has clean, distinct sortOrders. `rows` is already in the query's own
   * sortOrder ordering, so index ± 1 is the visible neighbor.
   */
  const handleMoveRow = async (index: number, direction: 'up' | 'down') => {
    const neighborIndex = direction === 'up' ? index - 1 : index + 1
    const current = rows[index]
    const neighbor = rows[neighborIndex]
    if (!current || !neighbor) return
    setBusyId(current.id)
    try {
      await upsertLookup({
        variables: { projectId, kind: current.kind, value: current.value, id: current.id, sortOrder: neighborIndex },
      })
      await upsertLookup({
        variables: { projectId, kind: neighbor.kind, value: neighbor.value, id: neighbor.id, sortOrder: index },
      })
      await afterMutate()
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleToggleArchived = async (row: LookupValue) => {
    setBusyId(row.id)
    try {
      await archiveLookup({ variables: { id: row.id, archived: !row.archived } })
      await afterMutate()
      toastr.success('', t(row.archived ? 'Submittals_lookup_unarchived_toast' : 'Submittals_lookup_archived_toast'))
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleAddNew = async () => {
    const trimmed = newValue.trim()
    if (!trimmed) return
    setBusyId('NEW')
    try {
      // Append at the end of the current tab's list so new values don't tie
      // at sortOrder 0 with everything already there (see handleMoveRow).
      await upsertLookup({ variables: { projectId, kind: tab, value: trimmed, sortOrder: rows.length } })
      await afterMutate()
      toastr.success('', t('Submittals_lookup_created'))
      setNewValue('')
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            {t('Submittals_lookups_title')}
          </DialogTitle>
        </DialogHeader>

        <Nav tabs>
          <NavItem>
            <NavLink
              className={cn('flex items-center', { active: dialogTab === 'lookups' })}
              onClick={() => setDialogTab('lookups')}
            >
              <ListChecks className="h-4 w-4 mr-2" />
              {t('Submittals_lookups_tab')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={cn('flex items-center', { active: dialogTab === 'templates' })}
              onClick={() => setDialogTab('templates')}
            >
              <Workflow className="h-4 w-4 mr-2" />
              {t('Submittals_wf_templates_tab')}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={dialogTab}>
          <TabPane tabId="lookups" className="border-0 bg-transparent p-0 shadow-none">
            <Nav tabs className="flex-wrap h-auto">
              {LOOKUP_TABS.map(({ kind, labelKey, Icon }) => (
                <NavItem key={kind}>
                  <NavLink
                    className={cn('flex items-center', { active: tab === kind })}
                    onClick={() => handleTabChange(kind)}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {t(labelKey)}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={tab}>
              {LOOKUP_TABS.map(({ kind }) => (
                <TabPane key={kind} tabId={kind} className="border-0 bg-transparent p-0 shadow-none">
                  <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
                    {loading ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">{t('Common_loading')}</div>
                    ) : rows.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">{t('Submittals_lookup_empty')}</div>
                    ) : (
                      rows.map((row, rowIndex) =>
                        editingId === row.id ? (
                          <div key={row.id} className="flex items-center gap-2 rounded border px-3 py-2">
                            <Input
                              autoFocus
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEdit(row)
                                if (e.key === 'Escape') cancelEdit()
                              }}
                              className="h-8 flex-1 text-sm"
                              disabled={!!busyId}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 shrink-0"
                              onClick={() => handleSaveEdit(row)}
                              disabled={!!busyId || !editValue.trim()}
                            >
                              {busyId === row.id ? (
                                <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Check className="h-3.5 w-3.5" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 shrink-0"
                              onClick={cancelEdit}
                              disabled={!!busyId}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <LookupRow
                            key={row.id}
                            row={row}
                            busy={busyId === row.id}
                            disabled={!!busyId || !!editingId}
                            canMoveUp={rowIndex > 0}
                            canMoveDown={rowIndex < rows.length - 1}
                            onMoveUp={() => handleMoveRow(rowIndex, 'up')}
                            onMoveDown={() => handleMoveRow(rowIndex, 'down')}
                            onStartEdit={() => startEdit(row)}
                            onToggleArchived={() => handleToggleArchived(row)}
                          />
                        ),
                      )
                    )}
                  </div>

                  <div className="mt-3 flex items-center gap-2 border-t pt-3">
                    <Input
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNew()}
                      placeholder={t('Submittals_lookup_add_placeholder')}
                      className="h-8 flex-1 text-sm"
                      disabled={!!busyId}
                    />
                    <Button
                      size="sm"
                      className="h-8 shrink-0"
                      onClick={handleAddNew}
                      disabled={!!busyId || !newValue.trim()}
                    >
                      {busyId === 'NEW' ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>
                </TabPane>
              ))}
            </TabContent>
          </TabPane>

          <TabPane tabId="templates" className="border-0 bg-transparent p-0 shadow-none">
            <WorkflowTemplatesTab projectId={projectId} />
          </TabPane>
        </TabContent>

        <div className="flex items-center justify-end pt-2">
          <Button variant="outline" onClick={handleClose} disabled={!!busyId}>
            {t('Common_close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SubmittalLookupsDialog
